import React, { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { validationNumber } from 'utils/validation';
import classes from './ArrRangeFilter.module.scss';

interface ArrRangeFilterProps {
  arrRangeMin: string;
  arrRangeMax: string;
  onChange: (field: string, value: string) => void;
  onClear: () => void;
}

const DEBOUNCE_DELAY = 500;

const ArrRangeFilter: React.FC<ArrRangeFilterProps> = ({
  arrRangeMin,
  arrRangeMax,
  onChange,
  onClear
}) => {
  const { t } = useTranslation(['activity']);
  const [localMin, setLocalMin] = useState(arrRangeMin);
  const [localMax, setLocalMax] = useState(arrRangeMax);

  // Sync local state from props (handles external resets like clear button in parent)
  useEffect(() => {
    if (arrRangeMin !== localMin) {
      setLocalMin(arrRangeMin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrRangeMin]);

  useEffect(() => {
    if (arrRangeMax !== localMax) {
      setLocalMax(arrRangeMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrRangeMax]);

  const debouncedOnChange = useMemo(
    () =>
      debounce((field: string, value: string) => {
        onChange(field, value);
      }, DEBOUNCE_DELAY),
    [onChange]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const getHasError = useCallback(
    (min: string, max: string) =>
      min !== '' && max !== '' && Number(min) > Number(max),
    []
  );

  const hasError = getHasError(localMin, localMax);

  const handleChange =
    (field: string, setLocal: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!validationNumber(e.target.value)) {
        const newValue = e.target.value;
        setLocal(newValue);

        const newMin = field === 'arrRangeMin' ? newValue : localMin;
        const newMax = field === 'arrRangeMax' ? newValue : localMax;
        if (!getHasError(newMin, newMax)) {
          debouncedOnChange(field, newValue);
        }
      }
    };

  const handleClear = () => {
    debouncedOnChange.cancel();
    setLocalMin('');
    setLocalMax('');
    onClear();
  };

  const isActive = localMin !== '' || localMax !== '';

  return (
    <div className={classes.wrapFilter}>
      <div className={classes.title}>{t('activity:featureFlag.ARR_RANGE')}</div>
      <div className={classes.container}>
        <input
          value={localMin}
          onChange={handleChange('arrRangeMin', setLocalMin)}
          placeholder={t('activity:featureFlag.MIN_ARR')}
          className={classes.input}
          type="number"
        />
        <span className={classes.separator}>{' - '}</span>
        <input
          value={localMax}
          onChange={handleChange('arrRangeMax', setLocalMax)}
          placeholder={t('activity:featureFlag.MAX_ARR')}
          className={classes.input}
          type="number"
        />
        {isActive && (
          <button
            className={classes.clearButton}
            onClick={handleClear}
            aria-label={t('activity:featureFlag.CLEAR_ARR_FILTER')}
          >
            ×
          </button>
        )}
      </div>
      {hasError && (
        <div className={classes.error}>
          {t('activity:featureFlag.MIN_LESS_THAN_MAX')}
        </div>
      )}
    </div>
  );
};

export default ArrRangeFilter;
